// atomConstants.ts

import { setInitialPlacementStatusMetrics } from '@client/core/atoms/metrics.js';
import { $prebidTargetingAtom } from '@client/core/atoms/prebidTargeting.js';
import { $wallpaperConfig } from '@client/core/atoms/wallpaperConfigAtom.js';
import {
  AdServer,
  type AfsPlacement,
  type ClientAdPlacement,
  ConsentStatus,
  devToolToggled,
  type InitialState,
  type MartailerPlacement,
  setAdvtState
} from '@schibsted-nmp/advertising-shared';

import { $afsAtom } from './afs.js';
import { $amplitudeExperimentsAtom } from './amplitude.js';
import {
  $config,
  $initialState,
  $ppidAtom,
  getEmptyOrPersistedPPID
} from './config.js';
import { $consentAtom } from './consent.js';
import { $gamTargetingAtom } from './gamTargeting.js';
import { $martailerAtom } from './martailer.js';
import {
  $placementsMap,
  $statusSequence,
  addStatusToSequence,
  getPlacementList,
  listenPlacementKeysForValueChange
} from './placements.js';
import { $unleashFeaturesAtom } from './unleashFeatures.js';

export function setInitialGlobalAtoms(
  initialState: InitialState,
  consent: ConsentStatus
) {
  $config.set(initialState.config);
  $ppidAtom.set(getEmptyOrPersistedPPID());
  $gamTargetingAtom.set(initialState.config.adServer.gam?.targeting ?? []);
  $prebidTargetingAtom.set(
    initialState.config.adServer.prebid?.targeting ?? []
  );
  $consentAtom.set(consent);
  $unleashFeaturesAtom.set(initialState.unleashFeatures);

  const allowedAdservers = [AdServer.Afs, AdServer.Gam, AdServer.Martailer];

  const allowedPlacements = initialState.config.placements.filter((placement) =>
    allowedAdservers.includes(placement.adServer.type as AdServer)
  );

  const placementsMap: Record<string, ClientAdPlacement> =
    allowedPlacements.reduce<Record<string, ClientAdPlacement>>(
      (map, placement) => {
        map[placement.placementId] = {
          ...placement,
          status: 'pending',
          creativeType: undefined,
          statusSequence: []
        } as const satisfies ClientAdPlacement;

        return map;
      },
      {}
    );

  if (devToolToggled() || initialState.env === 'local') {
    for (const placement of allowedPlacements) {
      listenPlacementKeysForValueChange(
        placement.placementId,
        'status',
        (status, oldStatus) => {
          if (status !== oldStatus) {
            addStatusToSequence(placement.placementId, status);
          }
        }
      );
    }
  }

  $placementsMap.set(placementsMap);

  $afsAtom.set({
    config: initialState.config.adServer.afs ?? null,
    placements: initialState.config.placements.filter(
      (placement): placement is AfsPlacement =>
        placement.adServer.type === AdServer.Afs
    )
  });

  const searchQuery = initialState.searchParams?.q;
  $martailerAtom.set({
    placements: initialState.config.placements.filter(
      (placement): placement is MartailerPlacement =>
        placement.adServer.type === AdServer.Martailer
    ),
    searchQuery
  });

  setInitialPlacementStatusMetrics(getPlacementList());

  $amplitudeExperimentsAtom.set(initialState.amplitudeExperiments);

  $initialState.set({
    env: initialState.env,
    baseUrl: initialState.baseUrl,
    logSessionId: initialState.logSessionId,
    locale: initialState.locale,
    unleashFeatures: initialState.unleashFeatures,
    amplitudeExperiments: initialState.amplitudeExperiments,
    config: initialState.config
  });
}

function makeState(): ADVT.State {
  function state() {
    return state as ADVT.State;
  }

  Object.defineProperties(state, {
    initialState: {
      get() {
        return $initialState.get();
      },
      enumerable: true
    },

    config: {
      get() {
        return $config.get();
      },
      enumerable: true
    },

    ppid: {
      get() {
        return $ppidAtom.get();
      },
      enumerable: true
    },

    placements: {
      get() {
        return $placementsMap.get();
      },
      enumerable: true
    },

    afs: {
      get() {
        return $afsAtom.get();
      },
      enumerable: true
    },

    unleashFeatures: {
      get() {
        return $unleashFeaturesAtom.get();
      },
      enumerable: true
    },

    gamTargeting: {
      get() {
        return $gamTargetingAtom.get();
      },
      enumerable: true
    },

    prebidTargeting: {
      get() {
        return $prebidTargetingAtom.get();
      },
      enumerable: true
    },

    statusSequence: {
      get() {
        return $statusSequence.get();
      },
      enumerable: true
    }
  } as const satisfies {
    [TKey in keyof ADVT.State]: PropertyDescriptor & {
      get(): ADVT.State[TKey];
    };
  });

  return state as ADVT.State;
}

setAdvtState('state', makeState());

// setWallpaperConfig will be removed once the creative is updated to post message to eventListener
function setWallpaperConfig(...args: Parameters<typeof $wallpaperConfig.set>) {
  $wallpaperConfig.set(...args);
}

setAdvtState('setWallpaperConfig', setWallpaperConfig);
